import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QaConsent, QaKey, QaRule, QaRun, QaRunDetail, QaSettings, QaUsage } from '@arcnet/contracts';
import { api } from './client.js';

/**
 * Auto QA (docs/37). One key root per account, so a save invalidates everything QA shows for it
 * and nothing else.
 */
const qaKey = (tenantId: string, ...rest: string[]) => ['qa', tenantId, ...rest] as const;

export const useQaSettings = (tenantId: string) =>
  useQuery({
    queryKey: qaKey(tenantId, 'settings'),
    queryFn: () => api.get<QaSettings>(`/v1/tenants/${tenantId}/qa/settings`),
    enabled: !!tenantId,
  });

export const useQaUsage = (tenantId: string) =>
  useQuery({
    queryKey: qaKey(tenantId, 'usage'),
    queryFn: () => api.get<QaUsage>(`/v1/tenants/${tenantId}/qa/usage`),
    enabled: !!tenantId,
  });

export const useQaKeys = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: qaKey(tenantId, 'keys'),
    queryFn: () => api.get<{ data: QaKey[] }>(`/v1/tenants/${tenantId}/qa/keys`),
    enabled: !!tenantId && enabled,
  });

export const useQaConsents = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: qaKey(tenantId, 'consents'),
    queryFn: () => api.get<{ data: QaConsent[]; statements: Record<string, string> }>(
      `/v1/tenants/${tenantId}/qa/consents`),
    enabled: !!tenantId && enabled,
  });

export const useQaRules = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: qaKey(tenantId, 'rules'),
    queryFn: () => api.get<{ data: QaRule[] }>(`/v1/tenants/${tenantId}/qa/rules`),
    enabled: !!tenantId && enabled,
  });

/**
 * Runs, refreshed while any are still working: a run moves from queued to complete in a minute or
 * two, and a list that has to be reloaded by hand to see it is a list nobody trusts.
 */
export const useQaRuns = (tenantId: string) =>
  useQuery({
    queryKey: qaKey(tenantId, 'runs'),
    queryFn: () => api.get<{ data: QaRun[] }>(`/v1/tenants/${tenantId}/qa/runs`),
    enabled: !!tenantId,
    refetchInterval: (q) =>
      (q.state.data?.data ?? []).some((r) => ['queued', 'transcribing', 'analysing'].includes(r.state))
        ? 10_000 : false,
  });

export const useQaRun = (tenantId: string, id: string | null) =>
  useQuery({
    queryKey: qaKey(tenantId, 'run', id ?? ''),
    queryFn: () => api.get<QaRunDetail>(`/v1/tenants/${tenantId}/qa/runs/${id}`),
    enabled: !!tenantId && !!id,
    refetchInterval: (q) =>
      q.state.data && ['queued', 'transcribing', 'analysing'].includes(q.state.data.state) ? 10_000 : false,
  });

export const useQaWhoami = (tenantId: string) =>
  useQuery({
    queryKey: qaKey(tenantId, 'whoami'),
    queryFn: () => api.get<{ reads_words: boolean; platform: boolean }>(`/v1/tenants/${tenantId}/qa/whoami`),
    enabled: !!tenantId,
    staleTime: Infinity,
  });

function useQaMutation<V, R = unknown>(tenantId: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['qa', tenantId] }); },
  });
}

export const useSaveQaSettings = (tenantId: string) =>
  useQaMutation(tenantId, (body: Record<string, unknown>) =>
    api.put(`/v1/tenants/${tenantId}/qa/settings`, body));

export const useSaveQaKey = (tenantId: string) =>
  useQaMutation(tenantId, ({ provider, ...body }: { provider: string } & Record<string, unknown>) =>
    api.put(`/v1/tenants/${tenantId}/qa/keys/${provider}`, body));

export const useRemoveQaKey = (tenantId: string) =>
  useQaMutation(tenantId, (provider: string) => api.delete(`/v1/tenants/${tenantId}/qa/keys/${provider}`));

export const useAcceptQaTerms = (tenantId: string) =>
  useQaMutation(tenantId, (provider: string) => api.post(`/v1/tenants/${tenantId}/qa/consents/${provider}`, {}));

export const useWithdrawQaTerms = (tenantId: string) =>
  useQaMutation(tenantId, (provider: string) => api.delete(`/v1/tenants/${tenantId}/qa/consents/${provider}`));

export const useSaveQaRule = (tenantId: string) =>
  useQaMutation(tenantId, ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
    id ? api.put(`/v1/tenants/${tenantId}/qa/rules/${id}`, body)
      : api.post(`/v1/tenants/${tenantId}/qa/rules`, body));

export const useDeleteQaRule = (tenantId: string) =>
  useQaMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/qa/rules/${id}`));

/** QA this call now (docs/37 §3.1). */
export const useQaThisCall = (tenantId: string) =>
  useQaMutation(tenantId, (cdrId: string) =>
    api.post<{ id: string; state: string }>(`/v1/tenants/${tenantId}/cdrs/${cdrId}/qa`, {}));

// ============================================================================ scorecards (Q2)

export const useQaScorecards = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: qaKey(tenantId, 'scorecards'),
    queryFn: () => api.get<{ data: import('@arcnet/contracts').QaScorecard[] }>(
      `/v1/tenants/${tenantId}/qa/scorecards`),
    enabled: !!tenantId && enabled,
  });

export const useSaveQaScorecard = (tenantId: string) =>
  useQaMutation(tenantId, ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
    id ? api.put(`/v1/tenants/${tenantId}/qa/scorecards/${id}`, body)
      : api.post(`/v1/tenants/${tenantId}/qa/scorecards`, body));

export const useDeleteQaScorecard = (tenantId: string) =>
  useQaMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/qa/scorecards/${id}`));

export const useQaPrompts = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: qaKey(tenantId, 'prompts'),
    queryFn: () => api.get<{ data: Record<string, string> }>(`/v1/tenants/${tenantId}/qa/prompts`),
    enabled: !!tenantId && enabled,
  });

export const useSaveQaPrompt = (tenantId: string) =>
  useQaMutation(tenantId, ({ kind, guidance }: { kind: string; guidance: string }) =>
    api.put(`/v1/tenants/${tenantId}/qa/prompts/${kind}`, { guidance }));

/** A supervisor's own answer to one question. Null puts the model's back. */
export const useOverrideQaScore = (tenantId: string, runId: string) =>
  useQaMutation(tenantId, ({ questionId, score, note }: { questionId: string; score: number | null; note?: string | null }) =>
    api.put<{ score: number | null; auto_failed: boolean }>(
      `/v1/tenants/${tenantId}/qa/runs/${runId}/scores/${questionId}`, { score, note: note ?? null }));
